import { createContainer } from "unstated-next";
import { useState, useCallback } from "react";
import * as voicifyApi from '../api';
import {
    color_colors_growth,
    color_colors_decline,
    color_colors_ocean,
    color_variants_decline_light_1,
    color_variants_ocean_light_1,
    highlight_green,
    color_variants_gold_light,
    color_colors_gold
} from '../constants/colors';
import { ALL_CATEGORIES } from '../constants/analyticsConstants/generativeCallReview';
import { FoodOrderingFeatureFlagId, HealthcareSchedulingFeatureFlagId } from "../constants/featureFlagIds";

// Consolidated S3 file containing all tag data
const TAG_DATA_FILE = "tag_display_data.json";

// Shape of the consolidated tag data file
interface TagDisplayData {
    positiveTags: string[];
    negativeTags: string[];
    cautionTags: string[];
    neutralTags: string[];
    foodOrderingTags: string[];
    healthcareTags: string[];
    tagDisplayNames: Record<string, string>;
}

// Color definitions (UI concern — not sourced from S3)
const neutralTagColors = {
    background: color_variants_ocean_light_1,
    color: color_colors_ocean,
    border: color_colors_ocean
};

const positiveTagColors = {
    background: highlight_green,
    color: color_colors_growth,
    border: color_colors_growth
};

const negativeTagColors = {
    background: color_variants_decline_light_1,
    color: color_colors_decline,
    border: color_colors_decline
};

const cautionTagColors = {
    background: color_variants_gold_light,
    color: color_colors_gold,
    border: color_colors_gold
};

function useTagConfigContainer() {
    const [positiveTags, setPositiveTags] = useState<string[]>([]);
    const [negativeTags, setNegativeTags] = useState<string[]>([]);
    const [cautionTags, setCautionTags] = useState<string[]>([]);
    const [neutralTags, setNeutralTags] = useState<string[]>([]);
    const [foodOrderingTags, setFoodOrderingTags] = useState<string[]>([]);
    const [healthcareTags, setHealthcareTags] = useState<string[]>([]);
    const [tagDisplayNames, setTagDisplayNames] = useState<Record<string, string>>({});
    const [isLoaded, setIsLoaded] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState("");

    const loadTagConfig = useCallback(async (applicationId: string) => {
        // Data is static — only load once per page load
        if (isLoaded || isLoading) {
            return;
        }

        setIsLoading(true);
        setError("");

        try {
            // Fetch consolidated tag data in a single call
            const result = await voicifyApi.getTagConfigData(applicationId, TAG_DATA_FILE);

            if (result.resultType !== "Ok" || !result.data) {
                console.error("Tag config fetch failed:", result);
                setError("Failed to load tag configuration");
                return;
            }

            // handleFetchResponse auto-parses JSON, so data may already be an object.
            // If it's still a string, parse it manually.
            let parsed: TagDisplayData;
            if (typeof result.data === "string") {
                try {
                    parsed = JSON.parse(result.data);
                } catch {
                    console.error("Failed to parse tag config JSON string");
                    setError("Failed to parse tag configuration");
                    return;
                }
            } else {
                parsed = result.data as unknown as TagDisplayData;
            }

            setPositiveTags(parsed.positiveTags || []);
            setNegativeTags(parsed.negativeTags || []);
            setCautionTags(parsed.cautionTags || []);
            setNeutralTags(parsed.neutralTags || []);
            setFoodOrderingTags(parsed.foodOrderingTags || []);
            setHealthcareTags(parsed.healthcareTags || []);
            setTagDisplayNames(parsed.tagDisplayNames || {});
            setIsLoaded(true);
        } catch (ex) {
            console.error("Error loading tag config:", ex);
            setError("Failed to load tag configuration");
        } finally {
            setIsLoading(false);
        }
    }, [isLoaded, isLoading]);

    // Derived: all tags in priority order
    const allTags = isLoaded
        ? [...positiveTags, ...neutralTags, ...negativeTags, ...cautionTags]
        : [];

    // Derived: all tags and categories combined (food ordering + healthcare deduplicated, plus categories)
    const allTagsAndCategories = isLoaded
        ? [...new Set([...foodOrderingTags, ...healthcareTags, ...ALL_CATEGORIES])]
        : [];

    // --- Helper functions that use loaded data ---

    const getTagName = useCallback((tag: string, _featureFlag?: string): string => {
        if (!isLoaded) {
            return tag;
        }
        console.log(_featureFlag)
        if (tag === "ACTION_COMPLETED") {
            switch (_featureFlag) {
                case "FOOD_ORDERING":
                    return "Order Placed";
                case "HEALTHCARE":
                    return "Appointment Booked";
                default:
                    return "Action Completed";
            }
        }

        return tagDisplayNames[tag] || tag;
    }, [isLoaded, tagDisplayNames]);

    const getTagPriority = useCallback((tag: string): number => {
        if (!isLoaded) {
            return 2; // default neutral
        }
        if (positiveTags.includes(tag)) {
            return 1;
        } else if (negativeTags.includes(tag)) {
            return 4;
        } else if (cautionTags.includes(tag)) {
            return 3;
        } else {
            return 2; // neutral / default
        }
    }, [isLoaded, positiveTags, negativeTags, cautionTags]);

    const sortTagsByPriority = useCallback((tags: string[], featureFlag?: string): string[] => {
        return [...tags].sort((a, b) => {
            const priorityA = getTagPriority(a);
            const priorityB = getTagPriority(b);

            if (priorityA === priorityB) {
                const displayNameA = getTagName(a, featureFlag) || a;
                const displayNameB = getTagName(b, featureFlag) || b;
                return displayNameA.localeCompare(displayNameB);
            }

            return priorityA - priorityB;
        });
    }, [getTagPriority, getTagName]);

    const getTagColors = useCallback((tag: string) => {
        if (!isLoaded) {
            return neutralTagColors;
        }
        if (positiveTags.includes(tag)) {
            return positiveTagColors;
        } else if (cautionTags.includes(tag)) {
            return cautionTagColors;
        } else if (negativeTags.includes(tag)) {
            return negativeTagColors;
        } else {
            return neutralTagColors;
        }
    }, [isLoaded, positiveTags, cautionTags, negativeTags]);

    const getTagCombinationPriority = useCallback((tags: string[] | undefined): number => {
        let total = 0;
        if (!tags || tags.length === 0) {
            return 100;
        }
        for (const tag of tags) {
            if (positiveTags.includes(tag)) {
                total++;
            }
            if (negativeTags.includes(tag)) {
                total += 4;
            }
            if (cautionTags.includes(tag)) {
                total += 3;
            }
            total += 2;
        }
        return total;
    }, [positiveTags, negativeTags, cautionTags]);

    return {
        // State
        positiveTags,
        negativeTags,
        cautionTags,
        neutralTags,
        foodOrderingTags,
        healthcareTags,
        tagDisplayNames,
        allTags,
        allTagsAndCategories,
        isLoaded,
        isLoading,
        error,

        // Actions
        loadTagConfig,

        // Helper functions
        getTagName,
        getTagPriority,
        sortTagsByPriority,
        getTagColors,
        getTagCombinationPriority
    };
}

const TagConfigContainer = createContainer(useTagConfigContainer);
export default TagConfigContainer;
export const useTagConfig = TagConfigContainer.useContainer;